import React, {memo, useEffect, useState} from "react";
import Picture from "./Picture";
import StoryCollection from "../../utils/StoryCollection";
import {useStoryCarousel} from "../../hooks/useStoryCarousel";
import {Link} from "react-router-dom";

interface TabDisplayConfig {
    showTabs: boolean;
    showDots: boolean;
}

const getTabDisplayConfig = (tabCount: number, screenWidth: number): TabDisplayConfig => {
    if (screenWidth < 640) {
        return {
            showTabs: tabCount <= 2,
            showDots: tabCount > 2
        };
    }

    if (screenWidth < 1024) {
        return {
            showTabs: tabCount <= 4,
            showDots: tabCount > 4
        }
    }

    if (screenWidth < 1280) {
        return {
            showTabs: tabCount <= 6,
            showDots: tabCount > 6
        }
    }

    if (tabCount <= 8) {
        return {
            showTabs: true,
            showDots: false
        }
    } else {
        return {
            showTabs: false,
            showDots: true
        }
    }
}

export const TabbedStories = memo(({id, title, stories, showTabs = true, cta}: {
    id: string,
    title: string,
    stories: StoryCollection,
    showTabs?: boolean,
    cta?: { title: string, href: string },
}) => {
    const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);

    const {
        storyKeys,
        activeStoryKey,
        activeSlideIndex,
        activeStorySlides,
        handleNextSlide,
        handlePrevSlide,
        handleTabClick,
        DURATION
    } = useStoryCarousel(stories);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const tabConfig = getTabDisplayConfig(storyKeys.length, screenWidth);

    if (!showTabs) {
        tabConfig.showTabs = false;
        tabConfig.showDots = false;
    }

    const activeTabIndex = storyKeys.indexOf(activeStoryKey);

    return (
        <section
            id={id}
            className="snap-section bg-ivory text-royal-pine flex flex-col"
        >
            <div className="flex flex-col h-full px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
                {/* Title */}
                <h2 className="heading-font text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-center mb-4 sm:mb-6 lg:mb-8 leading-tight px-2">
                    {title}
                </h2>

                {/* Tabs */}
                {tabConfig.showTabs && (
                    <div className="overflow-hidden rounded-t-lg shadow-lg">
                        <nav className="flex w-full">
                            {storyKeys.map(tabKey => (
                                <button
                                    key={tabKey}
                                    onClick={() => handleTabClick(tabKey)}
                                    className={`tab-font flex-1 py-2 sm:py-3 px-2 sm:px-4 font-medium text-xs sm:text-sm lg:text-base transition-all duration-300 hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 ${
                                        activeStoryKey === tabKey
                                            ? 'bg-royal-pine text-ivory '
                                            : 'bg-white text-royal-pine hover:bg-gray-100'
                                    }`}
                                    aria-pressed={activeStoryKey === tabKey}
                                >
                                    {tabKey.toUpperCase()}
                                </button>
                            ))}
                        </nav>
                    </div>
                )}

                {/* Title */}
                {tabConfig.showDots && (
                    <div
                        className="overflow-hidden rounded-t-lg shadow-lg bg-royal-pine text-ivory py-4 px-4 font-medium text-xs sm:text-sm">
                        {activeStoryKey.toUpperCase()}
                    </div>
                )}

                {/* Story Display Area */}
                <div
                    className={`relative h-64 sm:h-80 md:flex-1 min-h-0 bg-muted-sage overflow-hidden shadow-lg ${showTabs ? 'rounded-b-lg' : 'rounded-lg'}`}>
                    {/* Image with fade transition */}
                    {activeStorySlides.map((image, index) => (
                        <Picture
                            key={`${activeStoryKey}-${index}`}
                            {...(image.toLowerCase().endsWith('.webp') ? { webpSrc: image } : image.toLowerCase().endsWith('.png') ? { pngSrc: image } : { jpgSrc: image })}
                            alt={`${activeStoryKey} slide ${index + 1}`}
                            loading={index === 0 ? "eager" : "lazy"}
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ken-burns ${
                                activeSlideIndex === index ? 'opacity-100' : 'opacity-0'
                            }`}
                            onErrorSrc={'https://placehold.co/150x60/708978/F6F4E9?text=Story+Image'}
                        />
                    ))}

                    {/* Progress Indicators */}
                    <div
                        className="absolute top-2 sm:top-4 md:top-6 left-2 sm:left-4 md:left-6 right-2 sm:right-4 md:right-6 z-20">
                        <div className="flex space-x-1 sm:space-x-2">
                            {activeStorySlides.map((_, index) => (
                                <div
                                    key={index}
                                    className="flex-1 h-0.5 sm:h-1 bg-white bg-opacity-30 rounded-full overflow-hidden"
                                >
                                    <div
                                        key={`progress-${activeStoryKey}-${index}`}
                                        className={`h-full bg-white transition-all duration-200 ${
                                            index === activeSlideIndex
                                                ? 'story-progress'
                                                : index < activeSlideIndex ? 'w-full' : 'w-0'
                                        }`}
                                        style={{
                                            animationDuration: index === activeSlideIndex ? `${DURATION}ms` : `0ms`
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Interaction Area */}
                    <div className="absolute inset-0 flex z-10">
                        <button
                            className="w-1/2 h-full cursor-pointer focus:outline-none active:bg-royal-pine active:bg-opacity-10 transition-colors tap-highlight-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                handlePrevSlide();
                            }}
                            aria-label="Previous slide"
                        />
                        <button
                            className="w-1/2 h-full cursor-pointer focus:outline-none active:bg-royal-pine active:bg-opacity-10 transition-colors tap-highlight-none"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleNextSlide();
                            }}
                            aria-label="Next slide"
                        />
                    </div>

                    {/* Story Navigation */}
                    {(tabConfig.showDots || !showTabs) && storyKeys.length > 1 && (
                        <div className="absolute bottom-0 left-0 right-0 z-20">
                            <div
                                className="p-3 sm:p-4 md:p-6">
                                <div className="flex justify-between items-center">
                                    {/* Navigation Dots */}
                                    <div className="flex-1 flex">
                                        <div
                                            className="flex space-x-2 sm:space-x-3 bg-royal-pine/60 backdrop-blur-sm rounded-full px-3 sm:px-4 py-3">
                                            {storyKeys.map((tabKey, index) => (
                                                <button
                                                    key={tabKey}
                                                    onClick={() => handleTabClick(tabKey)}
                                                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 focus:outline-none ${activeTabIndex === index
                                                        ? 'bg-white shadow-sm'
                                                        : 'bg-white bg-opacity-50 hover:bg-opacity-75'}`}
                                                    aria-label={`Switch to ${tabKey} stories`}
                                                    title={tabKey}
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {/* Story Navigation Arrows */}
                                    <div className="flex space-x-2 ml-4">
                                        {cta && (
                                            <Link
                                                to={cta.href}
                                                onClick={() => {
                                                    const prevIndex = activeTabIndex === 0 ? storyKeys.length - 1 : activeTabIndex - 1;
                                                    handleTabClick(storyKeys[prevIndex]);
                                                }}
                                                className="px-4 py-2 text-ivory/80 hover:text-ivory backdrop-blur-sm bg-royal-pine/60 hover:bg-royal-pine/80 rounded-full transition-colors focus:outline-none"
                                                aria-label="Previous story"
                                            >
                                                {cta.title}
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
});

TabbedStories.displayName = 'TabbedStories';